import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserPlusIcon, Cog6ToothIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { Player, GameDetailData as Game } from '../types';
import AIChat from '../components/AIChat';

// Remove placeholder component as we now have the real implementation
// const AIChat = () => {
//   return (
//     <div className="bg-white p-6 rounded-lg shadow">
//       <h3 className="text-lg font-medium text-gray-900 mb-4">AI Game Assistant</h3>
//       <p className="mb-4">
//         This is where the AI chat component will be displayed. Implement the AIChat component to enable interactive conversations.
//       </p>
//       <p className="text-sm text-gray-500">
//         The component will be available at: src/frontend/components/AIChat.tsx
//       </p>
//     </div>
//   );
// };

export default function GameDetail() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { user: authUser } = useAuth();
  const [game, setGame] = useState<Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [newPlayer, setNewPlayer] = useState({ 
    name: '', 
    isGuest: true,
    userId: '',
    player_id: ''
  });
  const [newScore, setNewScore] = useState({ playerId: '', score: '' });
  // State for game settings form
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [gameSettingsForm, setGameSettingsForm] = useState({
    name: '',
    target_score: '', // Use string for input
    winning_mode: 'highest',
    points_type: 'allow_negative'
  });
  const [settingsError, setSettingsError] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  // Add specific loading states for player/score actions
  const [isAddingPlayerLoading, setIsAddingPlayerLoading] = useState(false);
  const [isAddingScoreLoading, setIsAddingScoreLoading] = useState(false);
  const [deletingPlayerId, setDeletingPlayerId] = useState<string | null>(null);
  const [deletingScoreId, setDeletingScoreId] = useState<string | null>(null);
  const [showAIChat, setShowAIChat] = useState(false);
  const [userHasAIAccess, setUserHasAIAccess] = useState(false);
  // Add state to track available players from collection
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);

  useEffect(() => {
    if (gameId) {
      fetchGame();
    }
  }, [gameId]);

  // Effect to update form state when game data loads
  useEffect(() => {
    if (game) {
      setGameSettingsForm({
        name: game.name,
        target_score: game.target_score?.toString() || '',
        winning_mode: game.winning_mode,
        points_type: game.points_type
      });
    }
  }, [game]); // Run when game data changes

  // Debugging useEffect - add this near the top of the component
  useEffect(() => {
    if (game && authUser) {
      console.log("Game Detail Debug:");
      console.log("- User:", authUser.username, "(ID:", authUser.id, ")");
      console.log("- Is Guest:", authUser.isGuest);
      console.log("- Is already player:", game.players.some(p => p.user_id === authUser.id));
      console.log("- Player list:", game.players.map(p => ({ id: p.id, name: p.name, user_id: p.user_id })));
    }
  }, [game, authUser]);

  // Add effect to fetch collection players
  useEffect(() => {
    if (game) {
      fetchCollectionPlayers(game.collection_id);
    }
  }, [game]);

  // Add function to fetch players from collection
  const fetchCollectionPlayers = async (collectionId: string) => {
    try {
      const response = await fetch(`/api/collections/${collectionId}/players`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Failed to fetch collection players');
        return;
      }
      
      const allPlayers = await response.json() as Player[];
      // Filter out players that are already in the game
      if (game) {
        const availablePlayers = allPlayers.filter(
          player => !game.players.some(gamePlayer => gamePlayer.id === player.id)
        );
        setAvailablePlayers(availablePlayers);
      }
    } catch (err) {
      console.error('Error fetching collection players:', err);
    }
  };

  const fetchGame = async () => {
    try {
      const response = await fetch(`/api/games/${gameId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Game not found');
        }
        throw new Error('Failed to fetch game');
      }

      const data = await response.json() as Game;
      setGame(data);
      
      // Also check if the user has AI access
      try {
        const userResponse = await fetch('/api/auth/me', {
          credentials: 'include',
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          // Use optional chaining and type checking to safely access the property
          setUserHasAIAccess(userData && typeof userData === 'object' && 'can_use_ai_assistant' in userData 
            ? Boolean(userData.can_use_ai_assistant) 
            : false);
        }
      } catch (err) {
        console.error("Failed to check AI access:", err);
        // Don't set an error - just disable the AI chat feature
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!game) return;
    setError('');
    setIsAddingPlayerLoading(true); // Set loading true
    try {
      const response = await fetch(`/api/games/${gameId}/players`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          player_id: newPlayer.player_id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData?.error || 'Failed to add player');
      }

      const data = await response.json() as Player;
      setGame(prev => prev ? {
        ...prev,
        players: [...prev.players, data],
      } : null);
      addNotification('Player added successfully!', 'success');
      setIsAddingPlayer(false);
      setNewPlayer({ name: '', isGuest: true, userId: '', player_id: '' });
      
      // Refresh available players
      if (game) {
        fetchCollectionPlayers(game.collection_id);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsAddingPlayerLoading(false); // Set loading false
    }
  };

  const handleAddScore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!game) return;
    const scoreValue = parseInt(newScore.score, 10);
    if (isNaN(scoreValue)) {
        setError('Score must be a valid number.');
        addNotification('Score must be a valid number.', 'error');
        return;
    }
    // Add validation against points_type if necessary
    if (game.points_type === 'positive_only' && scoreValue < 0) {
        setError('Negative scores are not allowed for this game.');
        addNotification('Negative scores are not allowed for this game.', 'error');
        return;
    }

    setError('');
    setIsAddingScoreLoading(true); 
    try {
      const response = await fetch(`/api/games/${gameId}/scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Submit score (number), not adjustment
        body: JSON.stringify({ playerId: newScore.playerId, score: scoreValue })
      });

      if (!response.ok) {
         const errorData = await response.json() as { error?: string };
         throw new Error(errorData?.error || 'Failed to add score');
      }

      addNotification('Score added!', 'success');
      setNewScore({ playerId: '', score: '' }); // Clear form input
      fetchGame(); // Re-fetch all game data including totals and history

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
        setIsAddingScoreLoading(false); 
    }
  };

  const handleDeletePlayer = async (playerId: string) => {
    if (!game) return;
    if (!window.confirm("Are you sure you want to remove this player and their scores from this game?")) return;
    setError('');
    setDeletingPlayerId(playerId); // Set ID
    try {
      const response = await fetch(`/api/games/${gameId}/players/${playerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete player');

      setGame(prev => prev ? {
        ...prev,
        players: prev.players.filter(p => p.id !== playerId),
        scoreHistory: prev.scoreHistory.filter(s => s.player_id !== playerId),
        scoreTotals: prev.scoreTotals.filter(st => st.player_id !== playerId)
      } : null);
      addNotification('Player removed.', 'success');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to remove player';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setDeletingPlayerId(null); // Clear ID
    }
  };

  const handleSettingsInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setGameSettingsForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingsRadioChange = (field: 'winning_mode' | 'points_type', value: string) => {
     setGameSettingsForm(prev => ({ ...prev, [field]: value }));
  }

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsError('');
    setIsSavingSettings(true);
    try {
      const settingsToUpdate = {
        ...gameSettingsForm,
        target_score: gameSettingsForm.target_score ? parseInt(gameSettingsForm.target_score, 10) : null
      };
      // Basic validation for target score
      if (isNaN(settingsToUpdate.target_score ?? 0)) {
          throw new Error('Target Score must be a number or empty.');
      }

      const response = await fetch(`/api/games/${gameId}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settingsToUpdate)
      });
      const data = await response.json();
      if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to update settings');
      
      // Merge the updated settings into the existing game state
      const updatedSettings = data as Partial<Game>; 
      setGame(prevGame => prevGame ? { ...prevGame, ...updatedSettings } : null);

      setIsEditingSettings(false);
      addNotification('Game settings updated!', 'success');

    } catch (err) {
       const errorMsg = err instanceof Error ? err.message : 'An error occurred updating settings';
       setSettingsError(errorMsg);
       addNotification(errorMsg, 'error');
    } finally {
       setIsSavingSettings(false);
    }
  };

  const handleDeleteScore = async (scoreId: string) => {
    if (!game) return;
    if (!window.confirm("Are you sure you want to delete this score entry?")) return;
    setError('');
    setDeletingScoreId(scoreId);
    try {
      const response = await fetch(`/api/games/${gameId}/scores/${scoreId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
         const errorData = await response.json() as { error?: string };
         throw new Error(errorData?.error || 'Failed to delete score');
      }
      addNotification('Score entry deleted.', 'success');
      fetchGame();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setDeletingScoreId(null);
    }
  };

  // Define Skeleton Components
  const SkeletonPlayerItem = () => (
    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md animate-pulse">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/5"></div>
      <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
    </div>
  );

  const SkeletonScoreItem = () => (
    <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md animate-pulse">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
    </div>
  );

  // Replace the player add form with a selector for existing collection players
  const renderPlayerAddForm = () => (
    <div className="mt-4 mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <h3 className="text-md font-semibold mb-3 text-gray-800 dark:text-gray-100">Add Player to Game</h3>
      <form onSubmit={handleAddPlayer} className="space-y-4">
        <div>
          <label htmlFor="player_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Select Player
          </label>
          <select
            id="player_id"
            value={newPlayer.player_id}
            onChange={(e) => {
              const selectedPlayer = availablePlayers.find(p => p.id === e.target.value);
              setNewPlayer(prev => ({ 
                ...prev, 
                player_id: e.target.value,
                name: selectedPlayer?.name || '',
                isGuest: Boolean(selectedPlayer?.is_guest)
              }));
            }}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
            required
          >
            <option value="">-- Select Player --</option>
            {availablePlayers.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name} {player.is_guest ? '(Guest)' : `(${player.username || 'Registered'})`}
              </option>
            ))}
          </select>
          {availablePlayers.length === 0 && (
            <p className="mt-2 text-sm text-red-500 dark:text-red-400">
              No players available to add. Create new players in the collection first.
            </p>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => setIsAddingPlayer(false)}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!newPlayer.player_id || isAddingPlayerLoading || availablePlayers.length === 0}
          >
            {isAddingPlayerLoading ? (
               <span className="flex items-center">
                 <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                 </svg>
                 Adding...
               </span>
            ) : ( 'Add Player' )}
          </button>
        </div>
      </form>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary dark:border-primary-light"></div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Game not found</h2>
        <button
          onClick={() => navigate('/collections')}
          className="mt-4 btn-primary"
        >
          Back to Collections
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
       {/* Header: Stack vertically on small screens */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{game.name}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Collection: {game.collection_name}
          </p>
        </div>
        <button
          onClick={() => navigate(`/collections/${game.collection_id}`)}
          className="btn-secondary w-full sm:w-auto" // Full width on mobile
        >
          Back to Collection
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {/* --- Game Configuration Section --- */}
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <Cog6ToothIcon className="h-5 w-5 mr-2 opacity-75"/> {/* Settings Icon */}
            Game Configuration
          </h2>
          {/* Add Edit/Cancel Buttons - Only show if user is owner/admin */}
          {/* TODO: Need collection role info here - Add fetch or pass down */}
          {/* { (collectionRole === 'owner' || collectionRole === 'admin') && ( */} 
            <button 
              onClick={() => setIsEditingSettings(!isEditingSettings)}
              className="btn-secondary text-sm"
            >
              {isEditingSettings ? 'Cancel' : 'Edit Settings'}
            </button>
          {/* )} */} 
        </div>

        {settingsError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-4">{settingsError}</p>
        )}

        {isEditingSettings ? (
          // EDITING VIEW (FORM)
          <form onSubmit={handleUpdateSettings} className="space-y-6">
             {/* Row 1: Game Name & Target Score */} 
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                 <label htmlFor="gameName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Game Name</label>
                 <input
                    type="text"
                    id="gameName"
                    name="name"
                    value={gameSettingsForm.name}
                    onChange={handleSettingsInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
              </div>
              <div>
                 <label htmlFor="targetScore" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Score (Optional)</label>
                 <input
                    type="number"
                    id="targetScore"
                    name="target_score"
                    value={gameSettingsForm.target_score}
                    onChange={handleSettingsInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                    placeholder="e.g., 100"
                    min="1"
                  />
              </div>
            </div>

             {/* Row 2: Winning Mode & Points Type */} 
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Winning Mode Radios */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Winning Mode</label>
                <div className="space-y-2">
                   {[ { value: 'highest', label: 'Highest Score Wins' }, { value: 'lowest', label: 'Lowest Score Wins' } ].map(opt => (
                      <button 
                         type="button"
                         key={opt.value}
                         onClick={() => handleSettingsRadioChange('winning_mode', opt.value)}
                         className={`w-full text-left p-3 rounded-md border flex items-center justify-between ${gameSettingsForm.winning_mode === opt.value ? 'bg-primary-light/20 border-primary dark:bg-primary-dark/30' : 'border-gray-300 dark:border-gray-600 dark:hover:bg-gray-700/50 hover:bg-gray-50'}`}
                       >
                        <span className="font-medium dark:text-gray-100">{opt.label}</span>
                        {gameSettingsForm.winning_mode === opt.value && <CheckCircleIcon className="h-5 w-5 text-primary dark:text-primary-light" />} 
                      </button>
                   ))}
                </div>
              </div>
               {/* Points Type Radios */} 
              <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Points Type</label>
                 <div className="space-y-2">
                   {[ { value: 'allow_negative', label: 'Allow Negative Points' }, { value: 'positive_only', label: 'Positive Points Only' } ].map(opt => (
                      <button 
                         type="button"
                         key={opt.value}
                         onClick={() => handleSettingsRadioChange('points_type', opt.value)}
                         className={`w-full text-left p-3 rounded-md border flex items-center justify-between ${gameSettingsForm.points_type === opt.value ? 'bg-primary-light/20 border-primary dark:bg-primary-dark/30' : 'border-gray-300 dark:border-gray-600 dark:hover:bg-gray-700/50 hover:bg-gray-50'}`}
                       >
                        <span className="font-medium dark:text-gray-100">{opt.label}</span>
                         {gameSettingsForm.points_type === opt.value && <CheckCircleIcon className="h-5 w-5 text-primary dark:text-primary-light" />} 
                       </button>
                   ))}
                 </div>
              </div>
            </div>
            
            {/* Save Button */} 
            <div className="flex justify-end pt-4">
                <button type="submit" className="btn-primary" disabled={isSavingSettings}>
                  {isSavingSettings ? (
                     <span className="flex items-center">
                       <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                       </svg>
                       Saving...
                     </span>
                   ) : ( 'Save Settings' )} 
                 </button>
             </div>
          </form>
        ) : (
          // DISPLAY VIEW
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <p><strong className="dark:text-gray-300">Target Score:</strong> <span className="dark:text-gray-100">{game.target_score ?? 'Not set'}</span></p>
                 <p><strong className="dark:text-gray-300">Winning Mode:</strong> <span className="dark:text-gray-100">{game.winning_mode === 'highest' ? 'Highest Score' : 'Lowest Score'}</span></p>
             </div>
             <p><strong className="dark:text-gray-300">Points Type:</strong> <span className="dark:text-gray-100">{game.points_type === 'allow_negative' ? 'Negative Allowed' : 'Positive Only'}</span></p>
           </div>
        )}
      </div>
      {/* --- END Game Configuration Section --- */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Players</h2>
            <div className="flex gap-2">
              {/* Debug output */}
              {authUser && (
                <div className="hidden">
                  User: {authUser.username} (ID: {authUser.id})
                  Is already player: {game.players.some(p => p.user_id === authUser.id) ? 'Yes' : 'No'}
                  Players: {JSON.stringify(game.players.map(p => ({ id: p.id, name: p.name, user_id: p.user_id })))}
                </div>
              )}
              {/* Add Myself Button - Only show if user is logged in, not already a player, and not adding other player */}
              {authUser && !authUser.isGuest && !isAddingPlayer && !game.players.some(p => p.user_id === authUser.id) && (
                <button
                  onClick={() => {
                    setError('');
                    setIsAddingPlayerLoading(true);
                    console.log("Adding myself as player, making request with:", {
                      name: authUser.username,
                      playerUserId: authUser.id,
                      url: `/api/collections/${game.collection_id}/players`
                    });
                    fetch(`/api/collections/${game.collection_id}/players`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({
                        name: authUser.username,
                        playerUserId: authUser.id
                      })
                    })
                    .then(response => {
                      console.log("Add myself response status:", response.status);
                      if (!response.ok) {
                        return response.json().then(data => {
                          console.error("Add myself error:", data);
                          throw new Error((data as {error?: string}).error || 'Failed to add yourself as player');
                        });
                      }
                      return response.json();
                    })
                    .then(data => {
                      console.log("Add myself success:", data);
                      const newPlayer = data as Player;
                      setGame(prev => prev ? {
                        ...prev,
                        players: [...prev.players, newPlayer],
                      } : null);
                      addNotification('You were added as a player!', 'success');
                    })
                    .catch(err => {
                      console.error("Add myself exception:", err);
                      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
                      setError(errorMsg);
                      addNotification(errorMsg, 'error');
                    })
                    .finally(() => {
                      setIsAddingPlayerLoading(false);
                      console.log("Add myself operation complete");
                    });
                  }}
                  className="btn-secondary text-sm"
                  disabled={isAddingPlayerLoading}
                >
                  {isAddingPlayerLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700 dark:text-gray-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </span>
                  ) : (
                    "Add Myself"
                  )}
                </button>
              )}
              <button
                onClick={() => setIsAddingPlayer(true)}
                className="btn-primary text-sm"
              >
                <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Add Player
              </button>
            </div>
          </div>

          {isAddingPlayer && renderPlayerAddForm()}

          <div className="space-y-2">
            {isLoading ? (
              [...Array(3)].map((_, i) => <SkeletonPlayerItem key={i} />)
            ) : game?.players?.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">No players yet</p>
            ) : (
              game?.players?.map((player) => {
                // Find total score for this player
                const playerTotal = game.scoreTotals?.find(st => st.player_id === player.id)?.total_score ?? 0;
                return (
                  <div key={player.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                    <div className="dark:text-gray-200 flex-grow">
                      <span className="font-medium dark:text-gray-100">{player.name}</span>
                      {/* Display Total Score Here */} 
                      <span className="ml-4 text-lg font-semibold dark:text-white bg-gray-100 dark:bg-gray-700 px-2 rounded">{playerTotal}</span> 
                      {player.username && (
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({player.username})</span>
                      )}
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        {player.is_guest ? 'Guest' : 'Registered'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeletePlayer(player.id)}
                      className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                      title="Remove Player"
                      disabled={deletingPlayerId === player.id || isAddingPlayerLoading}
                    >
                      {deletingPlayerId === player.id ? (
                         <svg className="animate-spin h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                         </svg>
                      ) : (
                         <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                         </svg>
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Scores Card - Refactored */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Score History</h2>
          
          {/* Add Score Form (submits absolute score) */} 
          <form onSubmit={handleAddScore} className="mb-6">
            {/* ... form elements similar to before, but input is for score, not adjustment ... */}
            <div className="flex gap-2">
              <select
                value={newScore.playerId}
                onChange={(e) => setNewScore(prev => ({ ...prev, playerId: e.target.value }))}
                className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                required
              >
                <option value="">Select Player</option>
                {game?.players?.map((player) => ( // Use optional chaining
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
              <input
                type="number" // Keep as number input
                placeholder="Score" // Placeholder text
                value={newScore.score} // Bind to score state
                onChange={(e) => setNewScore(prev => ({ ...prev, score: e.target.value }))} // Update score state
                className="w-24 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary focus:ring-primary sm:text-sm dark:bg-gray-700 dark:text-gray-100"
                required
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={!newScore.playerId || newScore.score === '' || isAddingScoreLoading}
              >
                {isAddingScoreLoading ? (
                     <span className="flex items-center">
                       <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                       </svg>
                       Adding...
                     </span>
                  ) : ( 'Add' )}
              </button>
            </div>
          </form>

          {/* Score History List */} 
          <div className="space-y-2">
            {isLoading ? (
               [...Array(5)].map((_, i) => <SkeletonScoreItem key={i} />)
            ) : game?.scoreHistory?.length === 0 ? ( // Check scoreHistory
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No scores recorded yet.</p>
            ) : (
              game?.scoreHistory?.map((entry) => ( // Map over scoreHistory
                <div key={entry.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                  <div className="dark:text-gray-200">
                    <span className="font-medium dark:text-gray-100">{entry.player_name}</span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">scored</span>
                    <span className="ml-2 font-semibold dark:text-gray-100">{entry.score}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(entry.created_at).toLocaleString()}
                    </span>
                    {/* Delete Button for individual entries */} 
                    <button
                      onClick={() => handleDeleteScore(entry.id)}
                      className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                      title="Delete Score Entry"
                      disabled={deletingScoreId === entry.id || isAddingScoreLoading}
                    >
                      {deletingScoreId === entry.id ? (
                        <svg className="animate-spin h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                         <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* --- AI Assistant Section --- */}
      {userHasAIAccess ? (
        showAIChat ? (
          <AIChat />
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <h2 className="text-xl font-semibold mb-4">Game Assistant</h2>
            <p className="mb-4 text-gray-600">
              Need help with strategy or have questions about this game?
            </p>
            <button
              onClick={() => setShowAIChat(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Chat with AI Assistant
            </button>
          </div>
        )
      ) : (
        <div className="bg-gray-100 p-6 rounded-lg border border-gray-300">
          <h2 className="text-xl font-semibold mb-4">Game Assistant</h2>
          <p className="text-gray-600">
            AI Game Assistant is a premium feature. Contact an administrator to enable this feature for your account.
          </p>
        </div>
      )}
      {/* --- END AI Assistant Section --- */}

    </div>
  );
} 